import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { fetchUserSignInMethods } from '../../actions/user'
import { fetchEmailVerificationPrompts } from '../../actions/emailVerification'
import {
  isEmailVerificationQAEnabled,
  getEmailVerificationQAState,
  setEmailVerificationQAState,
  resetEmailVerificationQAState,
  MOCK_BACKUP_EMAIL,
  deriveBackupEmail,
  MOCK_VERIFICATION_CODES,
  MOCK_INCORRECT_PASSWORD,
  EMAIL_EDIT_BUTTON_STYLES,
} from '../../utils/emailVerificationQA'

const styles = {
  panel: {
    position: 'fixed',
    bottom: 16,
    right: 16,
    width: 280,
    maxHeight: '80vh',
    overflowY: 'auto',
    background: '#1f2430',
    color: '#fff',
    fontSize: 12,
    lineHeight: 1.4,
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    zIndex: 999999,
    fontFamily: 'monospace',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    background: '#ffb020',
    color: '#1f2430',
    fontWeight: 'bold',
    borderRadius: '8px 8px 0 0',
    cursor: 'pointer',
  },
  body: { padding: 10 },
  section: { marginBottom: 12 },
  sectionTitle: { fontWeight: 'bold', marginBottom: 4, opacity: 0.8 },
  label: {
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, cursor: 'pointer',
  },
  select: {
    fontFamily: 'monospace', fontSize: 11, marginLeft: 4, maxWidth: '100%',
  },
  smallButton: {
    padding: '3px 8px',
    background: 'transparent',
    color: '#1f2430',
    border: '1px solid #1f2430',
    borderRadius: 4,
    cursor: 'pointer',
    fontFamily: 'monospace',
    fontSize: 11,
  },
}

// Dev/review-only helper: lets a reviewer configure which email verification
// scenario is active (which prompt is due, signin methods, reauth) without
// any of it hitting the real API, then walk the real flows (banner, account
// page) to see it. Always on — this container only ever renders on the
// dedicated email-verification review deployment. See emailVerificationQA.js.
function EmailVerificationQAPanelContainer(props) {
  const {
    user, dispatch, fetchUserSignInMethods, fetchEmailVerificationPrompts,
  } = props

  const [collapsed, setCollapsed] = useState(false)
  const [qaState, setQaState] = useState(() => getEmailVerificationQAState())

  // the app's normal boot sequence re-fetches the real user record on every
  // load/refetch (fetchUser isn't QA-gated, unlike signinMethods/prompts,
  // since it also carries plenty of non-mocked fields) — that would silently
  // clobber the mocked backupEmail/addBackupDismissCount back to whatever the
  // real account has. Reassert them here whenever the user object changes so
  // a reload, or any other real fetchUser dispatch, can't lose the mock —
  // reading qa state fresh (rather than trusting the qaState prop) so this
  // also catches addBackupDismissCount changes made outside the panel, e.g.
  // a real "Not now" click in the flow modal while it's open.
  useEffect(() => {
    if (!isEmailVerificationQAEnabled() || !user) { return }
    const liveQaState = getEmailVerificationQAState()
    const expectedBackupEmail = deriveBackupEmail(liveQaState)
    const expectedDismissCount = liveQaState.addBackupDismissCount || 0
    const actualDismissCount = (user.meta && user.meta.emailVerificationPromptState &&
      user.meta.emailVerificationPromptState.addBackupDismissCount) || 0
    const needsPatch = (user.backupEmail || null) !== expectedBackupEmail ||
      actualDismissCount !== expectedDismissCount
    if (needsPatch) {
      dispatch({
        type: 'UPDATE_USER_SUCCESS',
        response: {
          ...user,
          backupEmail: expectedBackupEmail,
          meta: {
            ...user.meta,
            emailVerificationPromptState: {
              ...(user.meta && user.meta.emailVerificationPromptState),
              addBackupDismissCount: expectedDismissCount,
            },
          },
        },
      })
    }
  }, [user, qaState.hasBackupEmail, qaState.addBackupDismissCount, dispatch])

  if (!isEmailVerificationQAEnabled()) { return null }

  function refetch() {
    fetchUserSignInMethods(user.id)
    fetchEmailVerificationPrompts(user.id)
  }

  function applyAndRefresh(partial) {
    setQaState(setEmailVerificationQAState(partial))
    refetch()
  }

  function setPassword(password) {
    applyAndRefresh({ password })
  }

  // an account can't have both Google and Apple signin, so this is a single
  // choice rather than two independent checkboxes
  function setExternalSignin(externalSignin) {
    applyAndRefresh({ externalSignin })
  }

  // verifyPrimary/affirmPrimary are mutually exclusive states of the same
  // primary email (not yet confirmed vs. confirmed-but-stale)
  function setPrimaryState(primaryState) {
    applyAndRefresh({ primaryState })
  }

  // toggling whether a backup email exists at all also patches the mocked
  // user.backupEmail (so screens that just read user.backupEmail directly,
  // e.g. RemoveBackupEmailContainer, stay in sync) and clears backupState,
  // since a 'due' value from before means something different once this flips
  function setHasBackupEmail(hasBackupEmail) {
    setQaState(setEmailVerificationQAState({ hasBackupEmail, backupEmail: null, backupState: 'none' }))
    dispatch({
      type: 'UPDATE_USER_SUCCESS',
      response: { ...user, backupEmail: hasBackupEmail ? MOCK_BACKUP_EMAIL : null },
    })
    refetch()
  }

  // meaning depends on hasBackupEmail — addBackup due (no backup yet) or
  // affirmBackup due (backup set but stale)
  function setBackupState(backupState) {
    applyAndRefresh({ backupState })
  }

  // manual override for testing "Don't ask me again" appearing once past its
  // threshold, without clicking "Not now" that many times for real
  function setAddBackupDismissCount(addBackupDismissCount) {
    applyAndRefresh({ addBackupDismissCount })
  }

  // only read when a success state schedules its auto-advance, so no refetch
  function toggleAutoAdvance() {
    setQaState(setEmailVerificationQAState({ autoAdvance: !qaState.autoAdvance }))
  }

  // purely visual, picked up live by EmailVerificationEmailRow — no refetch
  function setEditButtonStyle(editButtonStyle) {
    setQaState(setEmailVerificationQAState({ editButtonStyle }))
  }

  // purely visual, picked up live by EmailVerificationPrimaryOffer — no refetch
  function toggleVerifyPrimaryDescription() {
    setQaState(setEmailVerificationQAState({
      showVerifyPrimaryDescription: !qaState.showVerifyPrimaryDescription,
    }))
  }

  function toggleRequireReauth() {
    applyAndRefresh({ requireReauth: !qaState.requireReauth, reauthed: false })
  }

  function handleReset() {
    const next = resetEmailVerificationQAState()
    setQaState(next)
    dispatch({ type: 'UPDATE_USER_SUCCESS', response: { ...user, backupEmail: null } })
    refetch()
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header} onClick={() => setCollapsed(!collapsed)}>
        <span>⚡ Email Verification QA</span>
        <span>{collapsed ? '▸' : '▾'}</span>
      </div>
      {!collapsed && (
        <div style={styles.body}>
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Primary email</div>
            <select
              style={styles.select}
              value={qaState.primaryState}
              onChange={(e) => setPrimaryState(e.target.value)}
            >
              <option value='none'>confirmed, nothing due</option>
              <option value='verifyDue'>verifyPrimary due</option>
              <option value='affirmDue'>affirmPrimary due (stale)</option>
            </select>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Backup email</div>
            <label style={styles.label}>
              <input
                type='checkbox'
                checked={!!qaState.hasBackupEmail}
                onChange={(e) => setHasBackupEmail(e.target.checked)}
              />
              {`has a backup email set (${MOCK_BACKUP_EMAIL})`}
            </label>
            <select
              style={styles.select}
              value={qaState.backupState}
              onChange={(e) => setBackupState(e.target.value)}
            >
              <option value='none'>nothing due</option>
              {qaState.hasBackupEmail ?
                <option value='due'>affirmBackup due (stale)</option> :
                <option value='due'>addBackup due</option>}
            </select>
            <label style={{ ...styles.label, marginTop: 6 }}>
              add-backup dismiss count:
              <input
                type='number'
                min='0'
                style={{ ...styles.select, width: 50 }}
                value={qaState.addBackupDismissCount || 0}
                onChange={(e) => {
                  setAddBackupDismissCount(Math.max(0, parseInt(e.target.value, 10) || 0))
                }}
              />
            </label>
            <div style={{ opacity: 0.7 }}>
              &quot;Don&apos;t ask me again&quot; shows on the add-backup offer at 3+.
            </div>
          </div>

          <div style={{ opacity: 0.7, marginBottom: 12 }}>
            Reload the page after changing the above to see the banner
            auto-open the right screen for it.
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Verification code errors</div>
            <div style={{ opacity: 0.7 }}>
              On any code-entry screen, enter:
              <br />
              {MOCK_VERIFICATION_CODES.INCORRECT}
              {' '}
              → incorrect code
              <br />
              {MOCK_VERIFICATION_CODES.EXPIRED}
              {' '}
              → code expired
              <br />
              {MOCK_VERIFICATION_CODES.RATE_LIMITED}
              {' '}
              → rate limited
              <br />
              {MOCK_VERIFICATION_CODES.NO_LONGER_PENDING}
              {' '}
              → code no longer valid (discarded/already used)
              <br />
              {MOCK_VERIFICATION_CODES.SERVER_ERROR}
              {' '}
              → unexpected server error
              <br />
              any other code → succeeds
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Remove backup email errors</div>
            <div style={{ opacity: 0.7 }}>
              In the password prompt, enter:
              <br />
              {MOCK_INCORRECT_PASSWORD}
              {' '}
              → incorrect password
              <br />
              any other password → succeeds
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Success states</div>
            <label style={styles.label}>
              <input type='checkbox' checked={!!qaState.autoAdvance} onChange={toggleAutoAdvance} />
              auto-advance / auto-close after a few seconds
            </label>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Email edit button</div>
            <select
              style={styles.select}
              value={qaState.editButtonStyle}
              onChange={(e) => setEditButtonStyle(e.target.value)}
            >
              {Object.keys(EMAIL_EDIT_BUTTON_STYLES).map((style) => (
                <option key={style} value={style}>{EMAIL_EDIT_BUTTON_STYLES[style]}</option>
              ))}
            </select>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Verify primary screen</div>
            <label style={styles.label}>
              <input
                type='checkbox'
                checked={!!qaState.showVerifyPrimaryDescription}
                onChange={toggleVerifyPrimaryDescription}
              />
              show description copy
            </label>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Reauth</div>
            <label style={styles.label}>
              <input type='checkbox' checked={!!qaState.requireReauth} onChange={toggleRequireReauth} />
              require reauth for update/add-backup
            </label>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Signin methods</div>
            <label style={styles.label}>
              <input
                type='checkbox'
                checked={!!qaState.password}
                onChange={(e) => setPassword(e.target.checked)}
              />
              password
            </label>
            <label style={styles.label}>
              external signin:
              <select
                style={styles.select}
                value={qaState.externalSignin}
                onChange={(e) => setExternalSignin(e.target.value)}
              >
                <option value='none'>none</option>
                <option value='google'>google</option>
                <option value='apple'>apple</option>
              </select>
            </label>
          </div>

          <button type='button' style={styles.smallButton} onClick={handleReset}>Reset state</button>
        </div>
      )}
    </div>
  )
}

export default connect(
  (state) => ({ user: state.user }),
  (dispatch) => ({
    dispatch,
    ...bindActionCreators({ fetchUserSignInMethods, fetchEmailVerificationPrompts }, dispatch),
  }),
)(EmailVerificationQAPanelContainer)
